/**
 * แกนของหน้า /play — ส่งปุ่ม 30 Hz และเป็นคนตัดสินใจข้ามฉาก
 *
 * สิ่งที่จอย NES ทำไม่ได้แต่หน้านี้ทำได้: **เปลี่ยนชุดปุ่มตามฐานที่ยืนอยู่**
 * (ฐาน top-down ต้องการ 4 ทิศ · ฐาน platformer ต้องการ ←→ + กระโดด)
 */
import { clampWorldX, newCrossing, stepCrossing, type CrossingState } from "@py/engine";
import { Bus } from "@py/net";
import {
  ActMsg, EdgeMsg, encode, INPUT_HZ, MODE_BY_SCENE, PosMsg, ProfileMsg,
  SCENE_H, ScreenStatusMsg, safeParse, type SceneId, STYLE_BY_SCENE,
  clientIds, mobileSubscriptions, topics, GROUND_Y,
} from "@py/protocol";

import type { Session } from "./session.js";

export type ControllerEvents = {
  onPos?: (scene: SceneId, x: number, y: number) => void;
  onAct?: (msg: ActMsg) => void;
  onScreens?: (up: Set<SceneId>) => void;
  onStatus?: (s: "connecting" | "online" | "offline") => void;
};

export type Buttons = { l: 0 | 1; r: 0 | 1; u: 0 | 1; d: 0 | 1; j: 0 | 1; p: 0 | 1; k: 0 | 1 };

export class Controller {
  private readonly bus: Bus;
  private readonly cross: CrossingState;
  private readonly held: Buttons = { l: 0, r: 0, u: 0, d: 0, j: 0, p: 0, k: 0 };
  /** ตัวนับการกด — เพิ่มตอนกดลงเท่านั้น ห้ามเพิ่มตอนกดค้าง */
  private jp = 0;
  private pp = 0; // ต่อย
  private kp = 0; // เตะ
  private seq = 0;
  private inputTimer: ReturnType<typeof setInterval> | null = null;
  private readonly screensUp = new Set<SceneId>();
  private profile: ProfileMsg | null = null;

  constructor(
    private readonly session: Session,
    cfg: { url: string; username: string; password: string },
    private readonly ev: ControllerEvents = {},
  ) {
    this.cross = newCrossing(session.scene, session.x, session.y);
    this.bus = new Bus({
      ...cfg,
      clientId: clientIds.mobile(session.playerId),
      onStatus: (s) => {
        this.ev.onStatus?.(s);
        // ต่อใหม่แล้วต้องประกาศตัวเองซ้ำ ไม่งั้นจอที่รีสตาร์ตจะไม่รู้จักเรา
        if (s === "online") this.announce();
      },
    });
  }

  get scene(): SceneId {
    return this.cross.authScene;
  }

  get mode(): string {
    return MODE_BY_SCENE[this.cross.authScene];
  }

  start(profile: ProfileMsg | null): void {
    this.profile = profile;
    for (const f of mobileSubscriptions(this.session.playerId)) {
      this.bus.subscribe(f, (topic, payload) => this.onMessage(topic, payload));
    }
    this.bus.connect();

    // ส่ง input 30 Hz คงที่แม้ไม่มีการกด — NB ใช้เป็น heartbeat ตัดสินว่าผู้เล่นยังอยู่
    this.inputTimer = setInterval(() => this.sendInput(), 1000 / INPUT_HZ);
  }

  stop(): void {
    if (this.inputTimer) clearInterval(this.inputTimer);
    this.release(this.cross.authScene);
    this.bus.close();
  }

  /** ประกาศ profile (retained) แล้วเข้าฉากที่ค้างไว้ */
  private announce(): void {
    if (this.profile) {
      this.bus.publish(topics.profile(this.session.playerId), encode(this.profile), 1, true);
    }
    this.spawn(this.cross.authScene, this.cross.x, this.cross.y, true, this.session.scene === 0);
  }

  /**
   * ประกาศตัวละครเข้าฉาก — **retained** เหมือน `profile`
   *
   * ต้อง retained เพราะจอที่ต่อ broker ทีหลังไม่มีทางรู้ว่ามีผู้เล่นอยู่แล้ว:
   * `input` ที่ส่ง 30 Hz ถูกทิ้งทั้งหมดถ้าจอนั้นยังไม่มี player object
   * (`applyInput` คืนทันทีเมื่อไม่รู้จัก pid) ผลคือ **เปิดแท็บใหม่แล้วจอว่างเปล่า**
   * และจอจริงที่รีบูตกลางงานก็ไม่มีตัวละครอีกเลยจนกว่าผู้เล่นจะข้ามฉาก
   *
   * ค้างใน broker ไม่อันตราย: จอลบผู้เล่นที่ไม่มี input เกิน PLAYER_TIMEOUT_MS
   * อยู่แล้ว retained ที่ค้างจึงทำได้อย่างมากคือโชว์ตัวละคร 2 วินาทีแล้วหายไป
   */
  private spawn(scene: SceneId, x: number, y: number, auth: boolean, warp: boolean): void {
    // ฉากแนวข้าง: ห้ามส่งจุดเกิดใต้เส้นพื้น — session อาจจำค่ากลางการร่วงไว้
    // (เกิดใต้ one-way floor = ร่วงทะลุโลกตลอดกาล จอมีตาข่ายอีกชั้นแต่กันที่ต้นทางก่อน)
    if (MODE_BY_SCENE[scene] !== "topdown") y = Math.min(y, GROUND_Y);
    this.bus.publish(
      topics.spawn(scene, this.session.playerId),
      encode({
        p: this.session.playerId, s: scene,
        x: Math.round(x), y: Math.round(y),
        st: STYLE_BY_SCENE[scene], auth, warp, t: Date.now(),
      }),
      1,
      true,
    );
  }

  private release(scene: SceneId): void {
    this.bus.publish(
      topics.release(scene, this.session.playerId),
      encode({ p: this.session.playerId, s: scene, t: Date.now() }),
      1,
    );
    // ล้าง retained spawn ของฉากที่ออกมา ไม่งั้นจอที่เปิดทีหลังจะเห็นตัวละคร
    // โผล่ในฉากที่ผู้เล่นไม่ได้อยู่แล้ว (payload ว่าง = ลบ retained)
    this.bus.publish(topics.spawn(scene, this.session.playerId), "", 1, true);
  }

  press(btn: keyof Buttons, down: boolean): void {
    // ตัวนับเพิ่มเฉพาะ "ขอบกดลง" — QoS 0 จอจึงอ่านจากค่านับ ไม่ใช่บิตค้าง
    if (down && this.held[btn] === 0) {
      if (btn === "j") this.jp++;
      else if (btn === "p") this.pp++;
      else if (btn === "k") this.kp++;
    }
    this.held[btn] = down ? 1 : 0;
  }

  releaseAll(): void {
    this.held.l = this.held.r = this.held.u = this.held.d = 0;
    this.held.j = this.held.p = this.held.k = 0;
  }

  private sendInput(): void {
    this.bus.publish(
      topics.input(this.cross.authScene, this.session.playerId),
      encode({
        p: this.session.playerId, s: this.cross.authScene,
        l: this.held.l, r: this.held.r, u: this.held.u, d: this.held.d,
        j: this.held.j, jp: this.jp, pp: this.pp, kp: this.kp,
        seq: this.seq++, t: Date.now(),
      }),
      0,
    );
  }



  private onMessage(topic: string, payload: Uint8Array): void {
    if (topic === topics.pos(this.session.playerId)) {
      const m = safeParse(PosMsg, payload);
      if (m) this.onPos(m);
      return;
    }
    if (topic === topics.edge(this.session.playerId)) {
      const m = safeParse(EdgeMsg, payload);
      // edge เป็นแค่สัญญาณเตือน — การตัดสินใจจริงใช้ pos ที่แม่นกว่า
      if (m) this.onPos({ ...m, vx: 0, vy: 0, g: 1, a: "walk", f: 1 } as never);
      return;
    }
    if (topic === topics.act(this.session.playerId)) {
      const m = safeParse(ActMsg, payload);
      if (m) {
        this.session.score += m.v;
        this.ev.onAct?.(m);
      }
      return;
    }
    if (topic.endsWith("/status")) {
      const m = safeParse(ScreenStatusMsg, payload);
      if (!m) return;
      if (m.up) this.screensUp.add(m.s);
      else this.screensUp.delete(m.s);
      this.ev.onScreens?.(new Set(this.screensUp));
    }
  }

  private onPos(m: { s: SceneId; x: number; y: number }): void {
    const x = clampWorldX(m.s, m.x);
    const acts = stepCrossing(this.cross, { s: m.s, x, y: m.y });

    for (const a of acts) {
      if (a.kind === "spawn") this.spawn(a.scene, a.x, a.y, a.auth, a.warp);
      else if (a.kind === "release") this.release(a.scene);
    }

    // มือถือเก็บพิกัดในฐานะ database — ใช้ตอน reconnect
    this.session.scene = this.cross.authScene;
    this.session.x = this.cross.x;
    this.session.y = Math.min(this.cross.y, SCENE_H);
    this.ev.onPos?.(this.cross.authScene, this.cross.x, this.cross.y || GROUND_Y);
  }
}
